import React from "react";
import { StyledSelect } from "@components";
import { formatAmount, getMonth, cn } from "@utils";
import { Check2 } from "react-bootstrap-icons";

export const InvoiceRequestTypeSelector = ({
  control,
  overdueAmount,
  dueThisMonthAmount,
  allInvoicesAmount,
}) => {
  // Helper function to get the last date of the current month
  const getEndOfCurrentMonth = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return lastDay.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
    });
  };

  const renderOptions = (
    { label, amount, description },
    isSelected,
    disabled
  ) => (
    <div
      className={cn("flex justify-between w-full", disabled && "opacity-70")}
    >
      <div className="flex flex-col gap-1">
        <span
          className={cn(
            "font-medium",
            isSelected && !disabled ? "text-primary" : "text-zinc-600",
            disabled && "font-normal"
          )}
        >
          {label}
          {label !== "Any" && " · " + formatAmount(amount)}
        </span>
        <span className="text-zinc-500 text-xs whitespace-nowrap">
          {description}
        </span>
      </div>
      <span
        className={cn(
          "inline-flex items-center font-medium",
          isSelected && !disabled && "text-primary",
          disabled && "font-normal"
        )}
      >
        <span className="w-4">
          {isSelected && !disabled && <Check2 size={16} />}
        </span>
      </span>
    </div>
  );

  const renderDisplay = ({ label, amount }) => (
    <span className="flex justify-start w-full pr-1 select-none">
      <span className={cn("text-zinc-600")}>{label}</span>
      {label !== "Any" && (
        <span className={cn("text-zinc-900")}>
          &nbsp;·&nbsp;{formatAmount(amount)}
        </span>
      )}
    </span>
  );

  // None, Overdue, OverdueAndDueThisMonth, All
  const options = [
    {
      label: {
        label: "Overdue",
        description: "Request all invoices past their due date",
        amount: overdueAmount,
      },
      value: "Overdue",
      key: "Overdue",
      disabled: !overdueAmount || overdueAmount <= 0,
    },
    {
      label: {
        label: "Overdue + Due this month",
        description: `Request all overdue invoices and those due by ${getEndOfCurrentMonth()}`,
        amount: dueThisMonthAmount + overdueAmount,
      },
      value: "OverdueAndDueThisMonth",
      key: "OverdueAndDueThisMonth",
      disabled: !dueThisMonthAmount || dueThisMonthAmount <= 0,
    },
    {
      label: {
        label: "All Invoices",
        description:
          "Request all invoices (overdue, due this month, and in future months)",
        amount: allInvoicesAmount,
      },
      value: "All",
      key: "All",
      disabled:
        !(allInvoicesAmount - overdueAmount - dueThisMonthAmount) ||
        allInvoicesAmount - overdueAmount - dueThisMonthAmount <= 0,
    },
    {
      label: {
        label: "Any",
        description:
          "Customer can freely choose which invoices they would like to pay",
        // amount: 0,
      },
      value: "None",
      key: "None",
      disabled: !allInvoicesAmount || allInvoicesAmount <= 0,
    },
  ];

  return (
    <StyledSelect
      name="invoicesRequested"
      control={control}
      options={options}
      defaultSelected="Overdue"
      required={true}
      displayName="Invoices"
      rules={{
        required: {
          value: true,
          message: "Customer has no outstanding invoices",
        },
      }}
      renderOptions={renderOptions}
      renderDisplay={renderDisplay}
      width="full"
    />
  );
};
